// Identity API services

import {
  TenantRole,
  type User,
  type Invitation,
  type CreateInvitationRequest,
  type AcceptInvitationResult,
  type UpdateUserRoleRequest,
  type UpdateUserRoleResult,
  type ApiResult,
  type TenantMember,
  type RemoveMemberRequest,
} from '../types';
import { msalInstance, apiRequest as msalApiRequest } from '../../authentication';

/**
 * Base API URL - should be configured via environment variable
 */
const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://localhost:7001/api';

/**
 * Get authentication token from MSAL
 */
async function getAccessToken(): Promise<string> {
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) {
    return '';
  }

  try {
    const response = await msalInstance.acquireTokenSilent({
      ...msalApiRequest,
      account: accounts[0],
    });
    return response.accessToken;
  } catch (error) {
    console.warn('Silent token acquisition failed, redirecting to login:', error);
    try {
      await msalInstance.acquireTokenRedirect(msalApiRequest);
    } catch (redirectError) {
      console.error('Token redirect failed:', redirectError);
    }
    return '';
  }
}

/**
 * Make an authenticated API request
 */
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResult<T>> {
  const token = await getAccessToken();

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
    });

    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      return {
        isSuccess: false,
        message: response.ok ? 'Unexpected response format' : `Request failed (${response.status})`,
        statusCode: response.status,
      };
    }

    const data = await response.json();

    if (!response.ok) {
      return {
        isSuccess: false,
        message: data.message || 'An error occurred',
        statusCode: response.status,
      };
    }

    return {
      isSuccess: true,
      value: data.value ?? data,
      statusCode: response.status,
    };
  } catch (error) {
    return {
      isSuccess: false,
      message: error instanceof Error ? error.message : 'Network error',
      statusCode: 0,
    };
  }
}

// =============================================================================
// USER SERVICES
// =============================================================================

/**
 * Get the current authenticated user
 */
export async function getCurrentUser(): Promise<ApiResult<User>> {
  return apiRequest<User>('/identity/users/me');
}

/**
 * Get a user by ID
 */
export async function getUserById(userId: string): Promise<ApiResult<User>> {
  return apiRequest<User>(`/identity/users/${userId}`);
}

// =============================================================================
// ROLE SERVICES
// =============================================================================

/**
 * Update a user's role within a tenant
 */
export async function updateUserRole(
  request: UpdateUserRoleRequest
): Promise<ApiResult<UpdateUserRoleResult>> {
  return apiRequest<UpdateUserRoleResult>(
    `/identity/tenants/${request.tenantId}/roles/members/${request.userId}`,
    {
      method: 'PUT',
      body: JSON.stringify({ role: request.newRole }),
    }
  );
}

/**
 * Get available roles (local fallback)
 */
export function getAvailableRoles(): TenantRole[] {
  return [
    TenantRole.Owner,
    TenantRole.Admin,
    TenantRole.SecurityEngineer,
    TenantRole.Developer,
    TenantRole.Auditor,
    TenantRole.Viewer,
  ];
}

/**
 * Role info from the API
 */
export interface RoleInfo {
  name: string;
  displayName: string;
  description: string;
}

/**
 * Get available roles from the API with display names and descriptions
 */
export async function getAvailableRolesFromApi(
  tenantId: string
): Promise<ApiResult<RoleInfo[]>> {
  return apiRequest<RoleInfo[]>(`/identity/tenants/${tenantId}/roles/available`);
}

// =============================================================================
// TENANT MEMBER SERVICES
// =============================================================================

/**
 * Get all members of a tenant
 */
export async function getTenantMembers(
  tenantId: string
): Promise<ApiResult<TenantMember[]>> {
  return apiRequest<TenantMember[]>(`/identity/tenants/${tenantId}/roles/members`);
}

/**
 * Remove a member from a tenant
 */
export async function removeTenantMember(
  request: RemoveMemberRequest
): Promise<ApiResult<void>> {
  return apiRequest<void>(
    `/identity/tenants/${request.tenantId}/roles/members/${request.userId}`,
    {
      method: 'DELETE',
    }
  );
}

// =============================================================================
// INVITATION SERVICES
// =============================================================================

/**
 * Create a new invitation
 */
export async function createInvitation(
  request: CreateInvitationRequest
): Promise<ApiResult<Invitation>> {
  return apiRequest<Invitation>(
    `/identity/invitations`,
    {
      method: 'POST',
      body: JSON.stringify({
        tenantId: request.tenantId,
        email: request.email,
        role: request.role,
      }),
    }
  );
}

/**
 * Accept an invitation using the token
 */
export async function acceptInvitation(
  token: string
): Promise<ApiResult<AcceptInvitationResult>> {
  return apiRequest<AcceptInvitationResult>(`/identity/invitations/${token}/accept`, {
    method: 'POST',
  });
}

/**
 * Get pending invitations for a tenant
 */
export async function getTenantInvitations(
  tenantId: string
): Promise<ApiResult<Invitation[]>> {
  return apiRequest<Invitation[]>(`/identity/invitations/tenant/${tenantId}`);
}

/**
 * Revoke an invitation
 */
export async function revokeInvitation(
  _tenantId: string,
  invitationId: string
): Promise<ApiResult<void>> {
  return apiRequest<void>(
    `/identity/invitations/${invitationId}`,
    {
      method: 'DELETE',
    }
  );
}

/**
 * Resend an invitation
 */
export async function resendInvitation(
  _tenantId: string,
  invitationId: string
): Promise<ApiResult<Invitation>> {
  return apiRequest<Invitation>(
    `/identity/invitations/${invitationId}/resend`,
    {
      method: 'POST',
    }
  );
}

// =============================================================================
// ENTRA ID DIRECTORY SEARCH
// =============================================================================

/**
 * Entra ID user result from Microsoft Graph
 */
export interface EntraIdUser {
  id: string;
  displayName: string;
  mail: string | null;
  userPrincipalName: string;
  jobTitle?: string | null;
  department?: string | null;
}

/**
 * Search Entra ID directory for users via Microsoft Graph API.
 * Requires User.ReadBasic.All delegated permission on the app registration.
 */
export async function searchEntraIdUsers(query: string): Promise<EntraIdUser[]> {
  if (!query || query.length < 2) return [];

  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) return [];

  let graphToken: string;
  try {
    const response = await msalInstance.acquireTokenSilent({
      scopes: ['User.ReadBasic.All'],
      account: accounts[0],
    });
    graphToken = response.accessToken;
  } catch {
    // If silent fails, try popup (redirect would navigate away)
    try {
      const response = await msalInstance.acquireTokenPopup({
        scopes: ['User.ReadBasic.All'],
      });
      graphToken = response.accessToken;
    } catch (err) {
      console.warn('Failed to acquire Graph token for user search:', err);
      return [];
    }
  }

  try {
    const searchFilter = encodeURIComponent(
      `startsWith(displayName,'${query}') or startsWith(mail,'${query}') or startsWith(userPrincipalName,'${query}')`
    );
    const response = await fetch(
      `https://graph.microsoft.com/v1.0/users?$filter=${searchFilter}&$top=10&$select=id,displayName,mail,userPrincipalName,jobTitle,department`,
      {
        headers: {
          Authorization: `Bearer ${graphToken}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      // Fallback: try $search if $filter is not supported (requires ConsistencyLevel)
      const searchResponse = await fetch(
        `https://graph.microsoft.com/v1.0/users?$search="displayName:${query}" OR "mail:${query}"&$top=10&$select=id,displayName,mail,userPrincipalName,jobTitle,department`,
        {
          headers: {
            Authorization: `Bearer ${graphToken}`,
            'Content-Type': 'application/json',
            ConsistencyLevel: 'eventual',
          },
        }
      );
      if (!searchResponse.ok) return [];
      const searchData = await searchResponse.json();
      return searchData.value ?? [];
    }

    const data = await response.json();
    return data.value ?? [];
  } catch (err) {
    console.warn('Entra ID user search failed:', err);
    return [];
  }
}

// =============================================================================
// EXPORT ALL
// =============================================================================
export const identityService = {
  getCurrentUser,
  getUserById,
  updateUserRole,
  getAvailableRoles,
  getAvailableRolesFromApi,
  getTenantMembers,
  removeTenantMember,
  createInvitation,
  acceptInvitation,
  getTenantInvitations,
  revokeInvitation,
  resendInvitation,
  searchEntraIdUsers,
};
